import { Button, Group, Stack, Title, VisuallyHidden } from '@mantine/core';
import { ErrorAlert } from '@sportybot-web/components/alerts/error';
import { Pagination } from '@sportybot-web/components/pagination/pagination';
import { RulesTableFilters } from '@sportybot-web/features/rules/components/rules-table/components/rules-table-filters/rules-table-filters';
import { RulesTable } from '@sportybot-web/features/rules/components/rules-table/rules-table';
import {
  type RuleFilters,
  rulesSearchSchema,
} from '@sportybot-web/features/rules/schemas/rules-filters';
import { rulesQueryOptions } from '@sportybot-web/services/sporty-gateway/rules/queries';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  stripSearchParams,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { Plus } from 'lucide-react';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_authenticated/rules/')({
  component: RouteComponent,
  validateSearch: rulesSearchSchema,
  search: {
    middlewares: [stripSearchParams({ limit: 10, page: 0, allRules: true })],
  },
});
/* eslint-enable perfectionist/sort-objects */

function RouteComponent() {
  const options = useSearch({ from: Route.id });
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, error, isFetching, isLoading } = useQuery({
    ...rulesQueryOptions(options),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (page: number) => {
    return navigate({ replace: true, search: (current) => ({ ...current, page }) });
  };

  const handleChangeLimit = (limit: number) => {
    return navigate({ replace: true, search: (current) => ({ ...current, limit, page: 0 }) });
  };

  const handleChangeFilter = (filters: Partial<RuleFilters>) => {
    return navigate({ replace: true, search: (current) => ({ ...current, ...filters, page: 0 }) });
  };

  const { allRules, enabled, frequency, limit, page } = options;

  const rules = data?.rules?.rules ?? [];
  const total = data?.rules?.total ?? 0;

  return (
    <Stack>
      <VisuallyHidden>
        <Title>Rules</Title>
      </VisuallyHidden>
      {error && <ErrorAlert error={error} title="Rules Error" />}
      <RulesTable
        filters={
          <Group justify="space-between">
            <RulesTableFilters
              filters={{ allRules, enabled, frequency }}
              onChangeFilter={handleChangeFilter}
            />
            <Button
              component={Link}
              leftSection={<Plus size={16} />}
              ml="auto"
              radius="radius-max"
              size="xs"
              to="/rules/new"
              variant="default">
              Create Rule
            </Button>
          </Group>
        }
        loading={isLoading}
        pagination={
          <Pagination
            initialLoading={isLoading}
            limit={limit}
            loading={isFetching && !isLoading}
            onChangeLimit={handleChangeLimit}
            onChangePage={handleChangePage}
            page={page}
            selectLabel="Rows Per Page"
            selectOptions={[10, 25, 50]}
            total={total}
          />
        }
        rules={rules}
      />
    </Stack>
  );
}
